import { useState, useRef, useCallback, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const TIP_KEY = "np_voice_recorder_tip_seen";

/**
 * Simple voice recorder that captures webm/opus via MediaRecorder,
 * uploads to /schriftje/transcribe (Whisper) and returns text to onText().
 */
export default function VoiceRecorder({ onText, disabled }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const mountedRef = useRef(true);

  useEffect(() => {
    try {
      if (!localStorage.getItem(TIP_KEY)) setShowTip(true);
    } catch (e) { console.debug("localStorage niet beschikbaar:", e); }
  }, []);

  // Cleanup bij unmount: sluit mediaStream + stop recorder zodat de mic-icoon
  // niet blijft branden als de gebruiker weg-navigeert tijdens opname.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      try {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") {
          mr.ondataavailable = null;
          mr.onstop = null;
          mr.stop();
        }
      } catch (_) { /* ignore */ }
      try {
        streamRef.current?.getTracks().forEach(t => t.stop());
      } catch (_) { /* ignore */ }
      streamRef.current = null;
      mediaRecorderRef.current = null;
    };
  }, []);

  const dismissTip = () => {
    setShowTip(false);
    try { localStorage.setItem(TIP_KEY, "1"); } catch (e) { console.debug(e); }
  };

  const start = useCallback(async () => {
    if (recording || uploading) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microfoon niet beschikbaar in deze browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        // Voorkom state-updates op unmounted component
        if (!mountedRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mime });
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "recording.webm");
          const r = await api.post("/schriftje/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (!mountedRef.current) return;
          if (r.data?.text) {
            onText(r.data.text);
            toast.success("Transcriptie klaar");
          } else {
            toast.error("Geen tekst herkend, probeer opnieuw");
          }
        } catch (e) {
          if (mountedRef.current) toast.error(formatApiError(e.response?.data?.detail));
        }
        if (mountedRef.current) setUploading(false);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Geen toestemming voor microfoon");
    }
  }, [recording, uploading, onText]);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  }, []);

  return (
    <div className="inline-flex flex-col gap-2" data-testid="voice-recorder">
      <Button
        type="button"
        onClick={recording ? stop : start}
        disabled={disabled || uploading}
        data-testid={recording ? "voice-stop" : "voice-record"}
        className={`h-9 transition-colors ${recording ? "bg-red-600 hover:bg-red-700" : ""}`}
        style={recording ? undefined : { background: "var(--sage)" }}
      >
        {uploading
          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Transcriberen…</>
          : recording
            ? <><Square className="h-3 w-3 mr-1 animate-pulse" /> Stop opname</>
            : <><Mic className="h-3 w-3 mr-1" /> Spraak-invoer</>}
      </Button>
      {showTip && !recording && !uploading && (
        <div className="text-[11px] rounded-md border border-sky-200 bg-sky-50 text-sky-900 px-2.5 py-2 max-w-md flex items-start gap-2" data-testid="voice-tip">
          <span className="flex-1">
            <strong>Tip:</strong> Spreek in korte zinnen — noem <em>naam van het kind, activiteiten, eten, slaap, bijzonderheden</em>. Whisper doet de rest.
          </span>
          <button type="button" onClick={dismissTip} data-testid="voice-tip-close" className="text-sky-700 hover:text-sky-900 shrink-0">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
